<template>
  <div class="relative">
    <!-- Sidebar Toggle Button (Mobile) -->
    <button
      @click="toggleSidebar"
      class="fixed top-4 right-4 z-50 bg-gradient-to-r from-purple-700 to-blue-500 text-white p-3 rounded-lg flex items-center shadow-lg lg:hidden"
      aria-label="Toggle filter menu"
      aria-expanded="showSidebar"
      aria-controls="filter-navigation"
    >
      <Icon icon="mage:filter" class="text-xl" aria-hidden="true" />
    </button>

    <!-- Additional Navigation Button for Filters (LG Screens) -->
    <div class="hidden lg:flex lg:flex-col lg:space-y-4 fixed top-4 right-4 z-50">
      <!-- Filters -->
      <button
        @click="toggleSidebar"
        class="group relative flex items-center p-3 bg-gradient-to-r from-purple-700 to-blue-500 text-white rounded-lg shadow-lg transition-all duration-300 w-12 hover:w-40"
        aria-label="Toggle filter menu"
        aria-expanded="showSidebar"
      >
        <!-- Icon visible when not hovered -->
        <Icon icon="mage:filter" class="text-xl absolute group-hover:hidden" aria-hidden="true" />
        <!-- Icon and text visible on hover -->
        <Icon icon="mage:filter-fill" class="text-xl hidden group-hover:inline" aria-hidden="true" />
        <span
          class="ml-4 text-white transition-all duration-300 ease-in-out overflow-hidden group-hover:max-w-xs"
        >
          Filters
        </span>
      </button>
    </div>

    <!-- Sidebar Navigation -->
    <div
      :class="[
        'fixed top-0 right-0 h-full bg-gray-900 p-6 z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto',
        { 'translate-x-0': showSidebar, 'translate-x-full': !showSidebar },
      ]"
      style="width: 320px; max-width: 100vw; border-left: 2px solid rgba(255, 255, 255, 0.1)"
      id="filter-navigation"
      role="dialog"
      aria-label="Filter Options"
    >
      <!-- Close Button -->
      <div class="flex justify-between items-center mb-6">
        <h3 class="text-xl font-bold text-white">Filters</h3>
        <button
          @click="toggleSidebar"
          class="text-gray-400 focus:outline-none hover:text-white transition duration-200 transform hover:scale-105"
          aria-label="Close filter menu"
        >
          <Icon icon="mdi:close" class="text-[22px]" aria-hidden="true" />
        </button>
      </div>

      <!-- Search Input -->
      <div class="mb-6">
        <div class="flex items-center space-x-2 mb-2">
          <Icon icon="mdi:magnify" class="text-yellow-400 text-xl" aria-hidden="true" />
          <label for="search-input" class="text-white font-semibold">Search</label>
        </div>
        <input
          id="search-input"
          v-model="filters.search"
          type="text"
          placeholder="Search tools"
          class="w-full bg-gray-800 text-white rounded-lg py-2 px-3 focus:ring-2 focus:ring-yellow-400"
        />
      </div>

      <!-- Platform Filter -->
      <div class="mb-6">
        <div class="flex items-center space-x-2 mb-2">
          <Icon icon="mdi:devices" class="text-pink-500 text-xl" aria-hidden="true" />
          <label class="text-white font-semibold">Platform</label>
        </div>
        <div v-if="isLoadingFilters" class="p-4 flex justify-center">
          <div class="w-6 h-6 border-2 border-pink-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
        <div v-else class="grid grid-cols-2 gap-y-2">
          <div v-for="platform in sortedPlatforms" :key="platform.id" class="flex items-center space-x-2">
            <input
              type="checkbox"
              :id="`platform-${platform.id}`"
              :value="platform.name"
              v-model="filters.platform"
              class="bg-gray-700 text-pink-500 rounded focus:ring-0 h-5 w-5"
            />
            <label :for="`platform-${platform.id}`" class="text-gray-100">
              {{ platform.name }}
            </label>
          </div>
        </div>
      </div>

      <!-- Pricing Model Filter -->
      <div class="mb-6">
        <div class="flex items-center space-x-2 mb-2">
          <Icon icon="mdi:currency-usd-circle" class="text-green-400 text-xl" aria-hidden="true" />
          <label class="text-white font-semibold">Pricing</label>
        </div>
        <div class="grid grid-cols-2 gap-y-2">
          <div
            v-for="model in pricingModels"
            :key="model.value"
            class="flex items-center space-x-2"
          >
            <input
              type="checkbox"
              :id="`pricing-${model.value}`"
              :value="model.value"
              v-model="filters.pricing_model"
              class="bg-gray-700 text-green-400 rounded focus:ring-0 h-5 w-5"
            />
            <label :for="`pricing-${model.value}`" class="text-gray-100">{{ model.label }}</label>
          </div>
        </div>
      </div>
      
      <!-- Tags Filter -->
      <div class="mb-6">
        <div class="flex items-center space-x-2 mb-2">
          <Icon icon="mdi:tag-multiple" class="text-blue-400 text-xl" aria-hidden="true" />
          <label class="text-white font-semibold">Tags</label>
        </div>
        <div v-if="isLoadingFilters" class="p-4 flex justify-center">
          <div class="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
        <div v-else class="grid grid-cols-1 gap-y-2 max-h-48 overflow-y-auto pr-2">
          <div v-for="tag in sortedTags" :key="tag.id" class="flex items-center space-x-2">
            <input
              type="checkbox"
              :id="`tag-${tag.id}`"
              :value="tag.name"
              v-model="filters.tag"
              class="bg-gray-700 text-blue-400 rounded focus:ring-0 h-5 w-5"
            />
            <label :for="`tag-${tag.id}`" class="text-gray-100">
              {{ tag.name }}
            </label>
          </div>
        </div>
      </div>
      
      <!-- Features Filter -->
      <div class="mb-6">
        <div class="flex items-center space-x-2 mb-2">
          <Icon icon="mdi:puzzle" class="text-purple-400 text-xl" aria-hidden="true" />
          <label class="text-white font-semibold">Features</label>
        </div>
        <div v-if="isLoadingFilters" class="p-4 flex justify-center">
          <div class="w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
        <div v-else class="grid grid-cols-1 gap-y-2 max-h-48 overflow-y-auto pr-2">
          <div v-for="feature in sortedFeatures" :key="feature.id" class="flex items-center space-x-2">
            <input
              type="checkbox"
              :id="`feature-${feature.id}`"
              :value="feature.name"
              v-model="filters.feature"
              class="bg-gray-700 text-purple-400 rounded focus:ring-0 h-5 w-5"
            />
            <label :for="`feature-${feature.id}`" class="text-gray-100">
              {{ feature.name }}
            </label>
          </div>
        </div>
      </div>

      <!-- Apply Filters Button -->
      <div class="flex items-center gap-3">
        <button
          @click="applyFilters"
          class="flex-1 bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-500 hover:to-blue-500 text-white px-4 py-3 rounded-lg flex items-center justify-center"
        >
          <Icon icon="mdi:check-bold" class="text-xl mr-2" aria-hidden="true" />
          <span>Apply Filters</span>
        </button>
        
        <button
          @click="clearFilters"
          class="bg-gray-700 hover:bg-gray-600 text-white px-4 py-3 rounded-lg flex items-center justify-center transition-colors"
        >
          <Icon icon="mdi:refresh" class="text-xl" aria-hidden="true" />
        </button>
      </div>
    </div>

    <!-- Overlay (Behind Sidebar) -->
    <div
      :class="[
        'fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300 ease-in-out',
        { 'opacity-100': showSidebar, 'opacity-0 pointer-events-none': !showSidebar },
      ]"
      @click="toggleSidebar"
      aria-hidden="true"
    ></div>
  </div>
</template>

<script setup>
  import { ref, onMounted, onUnmounted, computed, watch } from 'vue';
  import { Icon } from '@iconify/vue';
  import { useForm } from '@inertiajs/vue3';
  import axios from 'axios';

  const props = defineProps({
    initialFilters: {
      type: Object,
      required: true,
    },
    useCase: {
      type: Object,
      required: false,
    }
  });

  const filters = ref({
    platform: props.initialFilters.platform || [],
    pricing_model: props.initialFilters.pricing_model || [],
    tag: props.initialFilters.tag || [],
    feature: props.initialFilters.feature || [],
    search: props.initialFilters.search || '',
  });

  const showSidebar = ref(false);
  const isLoadingFilters = ref(false);
  const platforms = ref([]);
  const tags = ref([]);
  const features = ref([]);

  const toggleSidebar = async () => {
    showSidebar.value = !showSidebar.value;
    
    // If opening sidebar and we don't have filter data yet, load it
    if (showSidebar.value && platforms.value.length === 0 && !isLoadingFilters.value) {
      await loadFilterData();
    }
  };

  const loadFilterData = async () => {
    isLoadingFilters.value = true;
    
    try {
      const response = await axios.get('/api/filter-options');
      platforms.value = response.data.platforms;
      tags.value = response.data.tags;
      features.value = response.data.features;
    } catch (error) {
      console.error('Error loading filter data:', error);
    } finally {
      isLoadingFilters.value = false;
    }
  };

  const pricingModels = [
    { value: 'free', label: 'Free' },
    { value: 'freemium', label: 'Freemium' },
    { value: 'paid', label: 'Paid' },
    { value: 'custom', label: 'Custom' },
  ];

  // Create sorted versions of the arrays
  const sortedPlatforms = computed(() => 
    platforms.value.length > 0 ? [...platforms.value].sort((a, b) => a.name.localeCompare(b.name)) : []
  );

  const sortedTags = computed(() => 
    tags.value.length > 0 ? [...tags.value].sort((a, b) => a.name.localeCompare(b.name)) : []
  );

  const sortedFeatures = computed(() => 
    features.value.length > 0 ? [...features.value].sort((a, b) => a.name.localeCompare(b.name)) : []
  );

  const applyFilters = () => {
    const currentPath = window.location.pathname;
    let routeName = '/tools';

    // Check if the current path matches a use case route
    if (props.useCase && currentPath.includes(`/tools/${props.useCase.slug}`)) {
      routeName = `/tools/${props.useCase.slug}`;
    }

    const form = useForm({
      platform: filters.value.platform,
      pricing_model: filters.value.pricing_model,
      tag: filters.value.tag,
      feature: filters.value.feature,
      search: filters.value.search,
    });

    form.get(routeName); // Navigate to the correct route
    toggleSidebar(); // Close sidebar after applying filters
  };
  
  const clearFilters = () => {
    filters.value = {
      platform: [],
      pricing_model: [],
      tag: [],
      feature: [],
      search: '',
    };
    
    applyFilters();
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      applyFilters();
    }
    
    // Close sidebar on escape key
    if (event.key === 'Escape' && showSidebar.value) {
      toggleSidebar();
    }
  };

  onMounted(() => {
    window.addEventListener('keydown', handleKeyDown);
  });

  onUnmounted(() => {
    window.removeEventListener('keydown', handleKeyDown);
  });
</script>

<style scoped>
  button {
    border: none;
    background-color: transparent;
  }

  .group {
    overflow: hidden;
    transition: width 0.3s ease;
  }

  .group:hover {
    width: 10rem;
  }

  .group .ml-4 {
    max-width: 0;
    transition: max-width 0.3s ease, opacity 0.3s ease;
  }

  .group:hover .ml-4 {
    max-width: 6rem;
    opacity: 1;
  }
  
  /* Scrollbar styling for tags and features lists */
  .overflow-y-auto::-webkit-scrollbar {
    width: 8px;
  }
  
  .overflow-y-auto::-webkit-scrollbar-track {
    background: rgba(31, 41, 55, 0.5);
    border-radius: 4px;
  }
  
  .overflow-y-auto::-webkit-scrollbar-thumb {
    background: rgba(75, 85, 99, 0.8);
    border-radius: 4px;
  }
  
  .overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background: rgba(107, 114, 128, 0.8);
  }
</style>